import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Stack, Typography, Tooltip, IconButton } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import ParentFacilityList from './ParentFacilityList';
import ParentForm from './ParentForm';

export default function ParentList() {
  const gridRef = useRef(null);

  const [parents, setParents] = useState([]);
  const [netTerms, setNetTerms] = useState([])
  const [parentInEdit, setParentInEdit] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openParentFacilityForm, setOpenParentFacilityForm] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get('/parent/parents')
      .then(res => {
        setParents(res.data.parents ?? []);
        setNetTerms(res.data.netTerms ?? [])
      })
      .catch(() => enqueueSnackbar('Parent list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);


  const deleteParent = (id) => {
    axiosInstance.post(`/parent/deleteParent?id=${id}`)
      .then(() => {
        setParents((currentParents) => currentParents.filter((parent) => parent.id !== id));
        enqueueSnackbar('Parent deleted', { variant: 'success' });
      })
      .catch(() => enqueueSnackbar('Parent delete failed', { variant: 'error' }));
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
      flex: 1
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={1}>
          <EditIconButton onClick={() => { setParentInEdit(params.data); setOpenForm(true); }} />
          <Tooltip title={'Configure Facilities'} arrow placement="top">
            <IconButton edge="end" color="secondary" onClick={() => { setParentInEdit(params.data); setOpenParentFacilityForm(true); }} disableRipple sx={{ px: 0 }}>
              <BuildIcon />
            </IconButton>
          </Tooltip>
          <DeleteIconButton content={`Are you sure you want to delete ${params.data.name}?`} action={() => deleteParent(params.data.id)} />
        </Stack>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Parent List | NHIDirect</title>
      </Helmet>
      <Box sx={{ height: 'calc(100% - 55px)', mt: 2 }}>
        <AGGridQuickFilterWrapper gridRef={gridRef}>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} color="primary" size="small" onClick={() => { setParentInEdit(null); setOpenForm(true); }}>
            <Typography>Add Parent</Typography>
          </Button>
        </AGGridQuickFilterWrapper>
        {!loading ? ( <Box sx={{ height: 'max(500px, 100%)' }}>
            <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={parents} />
          </Box> ) : ( <Box sx={{ display: 'flex', justifyContent: 'center', m: 3 }}> <CircularProgress /> </Box>)}
        <ParentForm netTerms={netTerms} parentInEdit={parentInEdit} setParents={setParents} open={openForm} close={() => { setParentInEdit(null); setOpenForm(false) }} />
        <ParentFacilityList parentInEdit={parentInEdit}  open={openParentFacilityForm} close={() => { setParentInEdit(null); setOpenParentFacilityForm(false); }}
        />
      </Box>
    </>
  );
}